import React from "react";
import styled from "styled-components";
import MarkdownMathText from "./MarkdownMathText";
import QuestionAssetPreviewStrip from "../document-processing/QuestionAssetPreviewStrip";
import { getHandoutFocusImages, getHandoutFocusSubsections } from "../../features/handoutFocusImages";
import { splitHandoutFocusChildren } from "../../features/handoutFocusChildren";

export default function HandoutFocusContent({ section, source, renderImage, wordLayout = false, bodyFontSize = 12, maxFloatWidthCm }) {
  const images = getHandoutFocusImages(section).map((image) =>
    image.layout === "float" && maxFloatWidthCm > 0
      ? { ...image, widthCm: Math.min(image.widthCm, maxFloatWidthCm) } : image);
  const textNode = (text) => !wordLayout ? <MarkdownMathText source={text} />
    : splitHandoutFocusChildren(text).map((part, index) => part.marker
      ? <FocusChildItem key={index} data-focus-child-item>
        <span>{part.marker}</span><div><MarkdownMathText source={part.source} /></div>
      </FocusChildItem>
      : <MarkdownMathText key={index} source={part.source} />);
  const imageNode = (image) => <FocusImage key={image.id} data-focus-image-id={image.id}
    data-focus-image-layout={image.layout} data-focus-image-alignment={image.alignment}
    $layout={image.layout} $alignment={image.alignment} style={{ width: `${image.widthCm}cm` }}>
    {renderImage ? renderImage(image) : <QuestionAssetPreviewStrip transparentBackground assets={[image.asset]} widthCm={image.widthCm} bodyFontSize={bodyFontSize} showCaption={false} />}
  </FocusImage>;
  if (images.some((image) => image.anchorSection !== undefined)) {
    const parts = getHandoutFocusSubsections(source);
    const anchored = (image) => Number.isInteger(image.anchorSection) && image.anchorSection >= 0 && image.anchorSection < parts.length;
    const legacyImages = images.filter((image) => !anchored(image));
    return <FocusContent data-focus-content>
      {legacyImages.filter((image) => image.layout === "float").map(imageNode)}
      {parts.map((part, index) => <FocusPart key={index} $wordLayout={wordLayout} data-focus-subsection={index} role="group" aria-label={part.title}>
        {images.filter((image) => anchored(image) && image.anchorSection === index && image.layout === "float").map(imageNode)}
        {textNode(part.source)}
        {images.some((image) => anchored(image) && image.anchorSection === index && image.layout === "block") && <FocusImageRows>
          {images.filter((image) => anchored(image) && image.anchorSection === index && image.layout === "block").map(imageNode)}
        </FocusImageRows>}
      </FocusPart>)}
      {legacyImages.some((image) => image.layout === "block") && <FocusImageRows>
        {legacyImages.filter((image) => image.layout === "block").map(imageNode)}
      </FocusImageRows>}
    </FocusContent>;
  }
  return <FocusContent data-focus-content>
    {images.filter((image) => image.layout === "float").map(imageNode)}
    {textNode(source)}
    {images.some((image) => image.layout === "block") && <FocusImageRows>
      {images.filter((image) => image.layout === "block").map(imageNode)}
    </FocusImageRows>}
  </FocusContent>;
}

const FocusContent = styled.div`
  display: flow-root;
  &::after { content: ""; display: block; clear: both; }
`;
const FocusPart = styled.div`
  display: flow-root;
  line-height: inherit;
  h3 { font-size: 1em; line-height: inherit; margin: 0.25em 0 0.15em; }
  && p { margin: 0; }
  ${({ $wordLayout }) => $wordLayout && `
    break-inside: avoid;
    h3 { margin: 0 0 4pt; break-after: avoid; }
    && p { margin: 0 0 4pt; }
  `}
`;
const FocusImage = styled.div`
  max-width: 100%;
  break-inside: avoid;
  page-break-inside: avoid;
  ${({ $layout, $alignment }) => $layout === "float" ? `
    float: ${$alignment === "left" ? "left" : "right"};
    margin: 0 ${$alignment === "left" ? "12pt 6pt 0" : "0 6pt 12pt"};
  ` : `
    margin: 6pt ${$alignment === "right" ? "0 6pt auto" : $alignment === "left" ? "auto 6pt 0" : "auto 6pt"};
  `}
  figure { width: 100%; max-width: 100%; margin: 0; border: 0; border-radius: 0; }
  figure img { width: 100%; height: auto; max-height: none; }
  > div { max-width: 100%; }
`;
const FocusImageRows = styled.div`clear: both;`;
const FocusChildItem = styled.div`
  display: grid;
  grid-template-columns: 0.75cm minmax(0, 1fr);
  margin-left: 0.75cm;
  line-height: inherit;
  > span { white-space: nowrap; }
  > div { min-width: 0; }
  && p { margin: 0 0 4pt; overflow: visible; }
`;
